<script setup>
import { ref, computed } from 'vue'
import { ChevronDown, ChevronRight, Layers, Plus, Trash2 } from 'lucide-vue-next'
import ChatMessagesView from '@/components/chat/ChatMessagesView.vue'
import ChatInput from '@/components/chat/ChatInput.vue'
import LayerDatePicker from '@/components/filters/LayerDatePicker.vue'
import { FEATURE_COUNT_WARNING_THRESHOLD, FEATURE_COUNT_CRITICAL_THRESHOLD } from '@/constants/features'

const props = defineProps({
  messages: {
    type: Array,
    default: () => []
  },
  isGenerating: {
    type: Boolean,
    default: false
  },
  connectedLayers: {
    type: Array,
    default: () => []
  },
  isStreaming: {
    type: Boolean,
    default: false
  },
  streamingMessage: {
    type: String,
    default: ''
  },
  orgId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['send-message', 'add-layer', 'remove-layer', 'layer-click'])

const showLayersPanel = ref(true)

const totalFeatures = computed(() => {
  return props.connectedLayers.reduce((sum, layer) => sum + (layer.features_count || 0), 0)
})

// Color coding for feature count badge
const featureCountColor = computed(() => {
  const count = totalFeatures.value
  if (count === 0) return 'text-gray-400'
  if (count < FEATURE_COUNT_WARNING_THRESHOLD) return 'text-green-600 bg-green-50'
  if (count <= FEATURE_COUNT_CRITICAL_THRESHOLD) return 'text-yellow-700 bg-yellow-50'
  return 'text-red-600 bg-red-50'
})

const featureCountWarning = computed(() => {
  const count = totalFeatures.value
  if (count > FEATURE_COUNT_CRITICAL_THRESHOLD) return 'High feature count - queries may be expensive'
  if (count >= FEATURE_COUNT_WARNING_THRESHOLD) return 'Approaching feature limit'
  return null
})

const handleSendMessage = (content) => {
  emit('send-message', content)
}

const handleAddLayer = () => {
  emit('add-layer')
}

const handleRemoveLayer = (layerCode) => {
  emit('remove-layer', layerCode)
}

const handleLayerClick = (layer) => {
  emit('layer-click', layer)
}
</script>

<template>
  <div class="flex flex-col bg-white h-full border-r border-gray-200 overflow-hidden">
    <!-- Layers Panel -->
    <div class="border-b border-gray-200 bg-gray-50 flex-shrink-0">
      <button
        @click="showLayersPanel = !showLayersPanel"
        class="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div class="flex items-center gap-2 flex-wrap">
          <Layers class="h-4 w-4 text-gray-600" />
          <span class="text-sm font-medium text-gray-700">Layers</span>
          <span class="text-xs text-gray-500">({{ connectedLayers.length }})</span>
          <LayerDatePicker />
          <span
            v-if="totalFeatures > 0"
            :class="featureCountColor"
            :title="featureCountWarning || `${totalFeatures} total features`"
            class="text-xs font-medium px-2 py-0.5 rounded-full"
          >
            {{ totalFeatures }} features
          </span>
        </div>
        <div class="flex items-center gap-2">
          <button
            @click.stop="handleAddLayer"
            class="p-1 hover:bg-gray-200 rounded transition-colors"
            title="Add layer"
          >
            <Plus class="h-4 w-4 text-gray-600" />
          </button>
          <ChevronDown v-if="showLayersPanel" class="h-4 w-4 text-gray-600" />
          <ChevronRight v-else class="h-4 w-4 text-gray-600" />
        </div>
      </button>

      <div v-if="showLayersPanel" class="px-3 py-2 text-sm text-gray-600 space-y-2 max-h-60 overflow-y-auto">
        <div v-if="connectedLayers.length === 0" class="text-gray-400 italic text-center py-4">
          No layers connected. Click the + button to add a layer.
        </div>

        <div v-else class="space-y-1">
          <div
            v-for="layer in connectedLayers"
            :key="layer.code"
            class="flex items-center justify-between p-2 hover:bg-gray-100 rounded transition-colors group"
          >
            <button
              @click="handleLayerClick(layer)"
              class="flex-1 text-left flex items-center gap-2"
            >
              <span class="font-medium text-gray-700 hover:text-blue-600">{{ layer.name }}</span>
              <span class="text-xs text-gray-400" v-if="layer.features_count !== undefined">
                ({{ layer.features_count }} features)
              </span>
            </button>
            <button
              @click="handleRemoveLayer(layer.code)"
              class="p-1 hover:bg-red-100 rounded transition-colors opacity-0 group-hover:opacity-100"
              title="Remove layer"
            >
              <Trash2 class="h-3 w-3 text-red-600" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Messages View -->
    <div class="flex-1 overflow-y-auto min-h-0">
      <ChatMessagesView
        :messages="messages"
        :is-generating="isGenerating"
        :is-streaming="isStreaming"
        :streaming-message="streamingMessage"
      />
    </div>

    <!-- Input Bar -->
    <div class="border-t border-gray-200 p-3 flex-shrink-0">
      <ChatInput
        placeholder="Ask me to analyze your data..."
        :disabled="isGenerating || connectedLayers.length === 0"
        @send="handleSendMessage"
      />
      <div v-if="connectedLayers.length === 0" class="text-xs text-gray-400 mt-1 text-center">
        Add at least one layer to start chatting
      </div>
    </div>
  </div>
</template>
